import { ZfPill, ZfStack, ZfTextButton } from '@zafire/react';
import moment from 'moment';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ISessionFilter } from '../../interfaces/client';
import { SessionDrawer } from './SessionDrawer';

const filterObj: Record<keyof ISessionFilter, string> = {
    clientFullName: 'sessionClientName',
    rmFullName: 'sessionRmName',
    countryCode: 'sessionCountry',
    updatedAtFromDate: 'updatedAtFromDate',
    updatedAtToDate: 'updatedAtToDate',
};

type SessionFilterPill = { key: string; value: string; value2?: string };

interface ISessionTableFilterProps {
    filters: ISessionFilter;
    onFilterChange: (filter: ISessionFilter) => void;
    availableCountryCodes: string[];
}

export const SessionTableFilter: FunctionComponent<ISessionTableFilterProps> = ({ filters, onFilterChange, availableCountryCodes }) => {
    const { t } = useTranslation();

    const [isDrawerOpen, setIsDrawerOpen] = useState(false);

    const isDateFilterKey = (key: keyof ISessionFilter) => ['updatedAtFromDate', 'updatedAtToDate'].includes(key);
    const formatDateString = (date: string) => moment(date).format('DD/MM/YYYY');
    const getDateFilterPill = useCallback(
        (dateFilterKeys: (keyof ISessionFilter)[]): SessionFilterPill => {
            if (dateFilterKeys.length === 2)
                return {
                    key: 'sessionDateRange',
                    value: formatDateString(filters[dateFilterKeys[0]]!),
                    value2: formatDateString(filters[dateFilterKeys[1]]!),
                };

            return { key: dateFilterKeys[0], value: formatDateString(filters[dateFilterKeys[0]]!) };
        },
        [filters],
    );

    const filterPills = useMemo<SessionFilterPill[]>(() => {
        const filterKeys = (Object.keys(filters) as (keyof ISessionFilter)[]).filter((key) => filters[key]);
        const dateFilterKeys = filterKeys.filter(isDateFilterKey);

        if (!filterKeys.length) return [];

        const dateFilterPills: SessionFilterPill[] = dateFilterKeys.length ? [getDateFilterPill(dateFilterKeys)] : [];

        return [...filterKeys.filter((key) => !isDateFilterKey(key)).map((key) => ({ key: filterObj[key], value: filters[key]! })), ...dateFilterPills];
    }, [filters, getDateFilterPill]);

    const onRemoveFilter = (pillKey: string) => {
        const removedFilter = Object.entries(filterObj).reduce(
            (acc, [key, label]) => {
                if (label === pillKey) acc[key as keyof ISessionFilter] = '';
                else if (pillKey === 'sessionDateRange') {
                    acc.updatedAtFromDate = '';
                    acc.updatedAtToDate = '';
                }

                return acc;
            },
            {} as Record<keyof ISessionFilter, string>,
        );
        onFilterChange({ ...filters, ...removedFilter });
    };

    return (
        <>
            <ZfStack direction="row" justify="space-between" alignment="center">
                <ZfTextButton onClick={() => setIsDrawerOpen(true)} size="medium" hierarchy="secondary" trailingIcon="filter_list">
                    {t('sessionFilter')}
                </ZfTextButton>
                <ZfStack direction="row" spacing="extra-small" style={{ flexShrink: 1 }}>
                    {filterPills.map((pill) => (
                        <ZfPill key={JSON.stringify(pill)} color="primary" deletable={true} onPillDismiss={() => onRemoveFilter(pill.key)}>
                            {t(`${pill.key}Pill`, { value: pill.value, value2: pill.value2 })}
                        </ZfPill>
                    ))}
                </ZfStack>
            </ZfStack>
            <SessionDrawer
                isOpen={isDrawerOpen}
                filters={filters}
                availableCountryCodes={availableCountryCodes}
                onSetFilter={onFilterChange}
                onClose={() => setIsDrawerOpen(false)}
            />
        </>
    );
};
